import { 
    Grid, 
    Box, 
    Typography, 
    Button, 
    TextField,
    Select,
    MenuItem
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Search, Add, Edit } from '@mui/icons-material';
import { Colors } from '../../../../../styles/theme';
import { sphereValues, cylinderValues, axisValues, addValues, formatForDisplay, parseForDatabase } from '../../../../../utils/rxValues';
import { usePreferences } from 'hooks/usePreferences';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';

export default function RxSection({ 
    selectedRx,
    setRxSelectDialogOpen,
    setCreateRxDialogOpen,
    onSaveRx,
    rxType = 'glasses' // Default to 'glasses'
}) {
    const { preferences } = usePreferences();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [draftRx, setDraftRx] = useState(selectedRx || null);

    useEffect(() => {
        setDraftRx(selectedRx || null);
        setIsEditing(false);
    }, [selectedRx]);

    const currentRx = useMemo(() => (isEditing ? draftRx : selectedRx), [isEditing, draftRx, selectedRx]);
    const practitionerDisplayName = useMemo(() => {
        if (!currentRx) {
            return '';
        }

        if (currentRx.practitionerSource === 'external') {
            return currentRx.externalPractitionerName || '';
        }

        const practitioner = currentRx.practitioner;
        if (practitioner?.fullName) {
            return practitioner.fullName;
        }

        const firstName = practitioner?.firstName || '';
        const lastName = practitioner?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) {
            return fullName;
        }

        return '';
    }, [currentRx]);

    const hasValue = (value) => value !== null && value !== undefined && value !== '';

    const updateField = (field, value) => {
        if (!isEditing) {
            return;
        }

        setDraftRx((prev) => ({
            ...(prev || {}),
            [field]: value,
        }));
    };

    const handleTextChange = (field) => (event) => {
        updateField(field, event.target.value);
    };

    const handleSelectChange = (field, parser) => (event) => {
        const { value } = event.target;
        if (value === '') {
            updateField(field, null);
            return;
        }
        updateField(field, parser(value));
    };

    const handleEdit = () => {
        if (!selectedRx) {
            return;
        }

        setDraftRx(selectedRx);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setDraftRx(selectedRx || null);
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (!draftRx || !onSaveRx) {
            setIsEditing(false);
            return;
        }

        try {
            setIsSaving(true);
            const updatedRx = await onSaveRx(draftRx);
            if (updatedRx) {
                setDraftRx(updatedRx);
            }
            setIsEditing(false);
        } catch (error) {
            // Parent handles user-visible error messaging.
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Grid container spacing={1} p={1} boxShadow={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1 }}>
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 1,
                        mb: 1,
                        p: 1,
                        backgroundColor: Colors.secondary,
                    }}
                >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            RX Details: {currentRx?.rxDate ? `Date: ${formatDateWithPreferences(currentRx.rxDate, preferences)}` : ''}
                            {currentRx?.expiryDate ? ` | Expires: ${formatDateWithPreferences(currentRx.expiryDate, preferences)}` : ''}
                            {rxType ? ` | Type: ${rxType.charAt(0).toUpperCase() + rxType.slice(1)}` : ''}
                        </Typography>
                        {practitionerDisplayName ? (
                            <Typography
                                variant='body2'
                                sx={{
                                    mt: 0.25,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Practitioner: {practitionerDisplayName}
                            </Typography>
                        ) : null}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'flex-end', md: 'flex-start' },
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        {isEditing ? (
                            <>
                                <Button
                                    size='small'
                                    variant='contained'
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    size='small'
                                    variant='outlined'
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    size='small'
                                    variant='contained'
                                    onClick={() => setRxSelectDialogOpen(true)}
                                    sx={{ minWidth: 36, px: 1 }}
                                >
                                    <Search fontSize='small' />
                                </Button>
                                {setCreateRxDialogOpen && (
                                    <Button
                                        size='small'
                                        variant='contained'
                                        startIcon={<Add />}
                                        onClick={() => setCreateRxDialogOpen(true)}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Create RX
                                    </Button>
                                )}
                                <Button
                                    size='small'
                                    variant='outlined'
                                    startIcon={<Edit />}
                                    onClick={handleEdit}
                                    disabled={!selectedRx}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        color: 'text.primary',
                                        borderColor: 'text.primary',
                                        '&:hover': {
                                            borderColor: 'text.primary',
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    Edit
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12}>
            <Typography variant='body2' sx={{mb: 1}}>Note: Updating RX values will update the original RX</Typography>
            </Grid>
            <Grid item xs={12}>
                <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>OD</Typography>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.odSph) ? formatForDisplay.sphere(currentRx.odSph) : ''}
                        onChange={handleSelectChange('odSph', parseForDatabase.sphere)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {sphereValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.odCyl) ? formatForDisplay.cylinder(currentRx.odCyl) : ''}
                        onChange={handleSelectChange('odCyl', parseForDatabase.cylinder)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {cylinderValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.odAxis) ? formatForDisplay.axis(currentRx.odAxis) : ''}
                        onChange={handleSelectChange('odAxis', parseForDatabase.axis)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.odAdd) ? formatForDisplay.add(currentRx.odAdd) : ''}
                        onChange={handleSelectChange('odAdd', parseForDatabase.add)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                    </Select>
                    <TextField label="Prism" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.odPrism || ''} onChange={handleTextChange('odPrism')} disabled={!isEditing} />
                    <TextField label="Base" variant="outlined" size="small" value={currentRx?.odPrismBase || ''} onChange={handleTextChange('odPrismBase')} disabled={!isEditing} />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>OS</Typography>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.osSph) ? formatForDisplay.sphere(currentRx.osSph) : ''}
                        onChange={handleSelectChange('osSph', parseForDatabase.sphere)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {sphereValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.osCyl) ? formatForDisplay.cylinder(currentRx.osCyl) : ''}
                        onChange={handleSelectChange('osCyl', parseForDatabase.cylinder)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {cylinderValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.osAxis) ? formatForDisplay.axis(currentRx.osAxis) : ''}
                        onChange={handleSelectChange('osAxis', parseForDatabase.axis)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={hasValue(currentRx?.osAdd) ? formatForDisplay.add(currentRx.osAdd) : ''}
                        onChange={handleSelectChange('osAdd', parseForDatabase.add)}
                        disabled={!isEditing}
                    >
                        <MenuItem value="">-</MenuItem>
                        {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                    </Select>
                    <TextField label="Prism" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.osPrism || ''} onChange={handleTextChange('osPrism')} disabled={!isEditing} />
                    <TextField label="Base" variant="outlined" size="small" value={currentRx?.osPrismBase || ''} onChange={handleTextChange('osPrismBase')} disabled={!isEditing} />
                </Box>
            </Grid>  

            {rxType === 'glasses' && (
                <>
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OD</Typography>
                            <TextField label="Dist PD" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.odDistPd || ''} onChange={handleTextChange('odDistPd')} disabled={!isEditing} />
                            <TextField label="Near PD" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.odNearPd || ''} onChange={handleTextChange('odNearPd')} disabled={!isEditing} />
                            <TextField label="Seg Ht." variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.odSegHt || ''} onChange={handleTextChange('odSegHt')} disabled={!isEditing} />
                            <TextField label="Sp. BC" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.odSpBc || ''} onChange={handleTextChange('odSpBc')} disabled={!isEditing} />
                            <TextField label="Vt. OC" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.odVtOc || ''} onChange={handleTextChange('odVtOc')} disabled={!isEditing} />
                            <TextField label="Lens Dia." variant="outlined" size="small" value={currentRx?.odLensDiameter || ''} onChange={handleTextChange('odLensDiameter')} disabled={!isEditing} />
                        </Box>
                    </Grid>               
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OS</Typography>
                            <TextField label="Dist PD" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.osDistPd || ''} onChange={handleTextChange('osDistPd')} disabled={!isEditing} />
                            <TextField label="Near PD" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.osNearPd || ''} onChange={handleTextChange('osNearPd')} disabled={!isEditing} />
                            <TextField label="Seg Ht." variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.osSegHt || ''} onChange={handleTextChange('osSegHt')} disabled={!isEditing} />
                            <TextField label="Sp. BC" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.osSpBc || ''} onChange={handleTextChange('osSpBc')} disabled={!isEditing} />
                            <TextField label="Vt. OC" variant="outlined" sx={{ mr: 1 }} size="small" value={currentRx?.osVtOc || ''} onChange={handleTextChange('osVtOc')} disabled={!isEditing} />
                            <TextField label="Lens Dia." variant="outlined" size="small" value={currentRx?.osLensDiameter || ''} onChange={handleTextChange('osLensDiameter')} disabled={!isEditing} />
                        </Box>
                    </Grid>
                </>
            )}

            {rxType === 'contacts' && (
                <>
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OD</Typography>
                            <TextField label="BC" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.odBc || ''} onChange={handleTextChange('odBc')} disabled={!isEditing} />
                            <TextField label="Dia" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.odContactLensDiameter || ''} onChange={handleTextChange('odContactLensDiameter')} disabled={!isEditing} />
                            <TextField label="K1" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.odK1 || ''} onChange={handleTextChange('odK1')} disabled={!isEditing} />
                            <TextField label="K2" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.odK2 || ''} onChange={handleTextChange('odK2')} disabled={!isEditing} />
                            <TextField label="K-Axis" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.odKAxis || ''} onChange={handleTextChange('odKAxis')} disabled={!isEditing} />
                            <TextField label="VA" variant="outlined" sx={{ width: '80px' }} size="small" value={currentRx?.odVa || ''} onChange={handleTextChange('odVa')} disabled={!isEditing} />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OS</Typography>
                            <TextField label="BC" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.osBc || ''} onChange={handleTextChange('osBc')} disabled={!isEditing} />
                            <TextField label="Dia" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.osContactLensDiameter || ''} onChange={handleTextChange('osContactLensDiameter')} disabled={!isEditing} />
                            <TextField label="K1" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.osK1 || ''} onChange={handleTextChange('osK1')} disabled={!isEditing} />
                            <TextField label="K2" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.osK2 || ''} onChange={handleTextChange('osK2')} disabled={!isEditing} />
                            <TextField label="K-Axis" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={currentRx?.osKAxis || ''} onChange={handleTextChange('osKAxis')} disabled={!isEditing} />
                            <TextField label="VA" variant="outlined" sx={{ width: '80px' }} size="small" value={currentRx?.osVa || ''} onChange={handleTextChange('osVa')} disabled={!isEditing} />
                        </Box>
                    </Grid>
                </>
            )}
        </Grid>
    );
}